// dependencies
import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import sendAnalytics from '@st-operations/uikit/lib/sendAnalytics.js'
import { Button, BUTTON_THEME, Input, Select } from '@st-operations/uikit/src/v2/index.js'
import { getRegions } from '_services/regions'
import MultiValueInput from '../multi-value-input'
import csvToJson from '_lib/csvToJson'

import './styles.scss'
import FileInput from '../file-input'
import { logErrorAsync } from '_lib/utility'

const ENTITY = 'url-similarity-seeds'

const CreateUrlSimilaritySeedsModal = ({
  value,
  open,
  title = 'New URL Similarity Seed',
  onClose = () => {},
}) => {
  const [name, setName] = useState('')
  const [selectedRegions, setSelectedRegions] = useState([])
  const [urls, setUrls] = useState([])
  const [file, setFile] = useState()
  const [regions, setRegions] = useState([])

  const inputRef = useRef()

  useEffect(() => {
    if (value) {
      setName(value)
    }
  }, [value])

  useEffect(() => {
    if (open) {
      inputRef.current.focus()
    }
  }, [open])

  useEffect(() => {
    if (!file) return
    const reader = new FileReader()
    reader.addEventListener(
      'load',
      () => {
        try {
          const json = csvToJson(reader.result)
          if (json[0]?.url) {
            setUrls(json.map((m) => m.url).filter((f) => f))
          } else {
            throw new Error()
          }
        } catch (err) {
          toast.error(`File must be a CSV with a field called 'url'`)
        }
      },
      false
    )
    reader.readAsText(file)
  }, [file])

  useEffect(() => {
    async function getData() {
      try {
        const regionsResponse = await getRegions({
          fields: ['_id', 'name', 'countries'],
          limit: 1000,
        })

        setRegions(regionsResponse.items.sort((a, b) => (a.name > b.name ? 1 : -1)))
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }
    getData()
  }, [])

  const onCreate = () => {
    if (!urls || urls.length === 0) {
      toast.error(`missing 'urls'`)
      return
    }

    window._api.post(
      `/${ENTITY}`,
      { name, regions: selectedRegions, urls: urls.filter((f) => f) },
      (error, item) => {
        if (error) {
          toast.error(error.message)
        } else {
          onClose()
          window._history.push(`/${ENTITY}/${item._id}`)
        }

        // event tracking
        sendAnalytics('create', ENTITY, item)
      }
    )
  }

  const onDismiss = () => {
    onClose()
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onCreate()
    }
    if (e.key === 'Escape' || (e.metaKey && e.key === 'e')) {
      e.preventDefault()
      onDismiss()
    }
  }

  const updateName = (value) => {
    setName(value)
  }

  return (
    <section className="st-new-entity-modal">
      <If condition={title}>
        <h1 className="st-new-entity-modal__title">{title}</h1>
      </If>
      <Input ref={inputRef} onChange={updateName} onKeyDown={onKeyDown} value={name} label="Name" />
      <Select
        label="Regions"
        className="audience-creator__regions-field"
        isMulti
        isSearchable
        useReactSelect
        name="regions"
        options={regions?.filter(
          (region) =>
            !selectedRegions?.some((selectedRegion) => selectedRegion.name === region.name)
        )}
        value={selectedRegions?.sort((a, b) => (a.name > b.name ? 1 : -1))}
        onChange={setSelectedRegions}
      />
      <MultiValueInput
        className="create-url-similarity-seeds-modal__multivalue mb-8"
        label="URLs"
        values={urls}
        onChange={(value) => setUrls(value)}
      />
      <FileInput onAddFile={setFile} />
      <div className="mt-4 flex justify-end">
        <Button
          className="st-new-entity-modal__button"
          theme={BUTTON_THEME.SECONDARY}
          onClick={onDismiss}
        >
          Back
        </Button>
        <Button className="st-new-entity-modal__button" onClick={onCreate}>
          Create
        </Button>
      </div>
    </section>
  )
}

CreateUrlSimilaritySeedsModal.propTypes = {
  title: PropTypes.string,
  label: PropTypes.string,
  open: PropTypes.bool,
  defaultProperties: PropTypes.shape(),
  urlRedirect: PropTypes.string,
  entities: PropTypes.string,
  value: PropTypes.string,
  onClose: PropTypes.func,
}

CreateUrlSimilaritySeedsModal.defaultProperties = {
  title: '',
  label: 'Name',
  open: false,
  defaultProperties: {},
  urlRedirect: '',
  entities: '',
  value: '',
  onClose: () => {},
}

// export
export default React.memo(CreateUrlSimilaritySeedsModal)
